// dependencies
// charting components
import {
	BarElement,
	CategoryScale,
	ChartData,
	Chart as ChartJS,
	ChartOptions,
	LinearScale,
	Title,
	Tooltip,
} from "chart.js";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
// components
import Text from "../../../../../../components/Text";
import { MonthLabel, getParticipationDateRangeLabels } from "./ParticipationUtils";

// store
import useStore from "../../../../store";

// api
import { useParticipationRate } from "../../../../../../api/hooks";

// utils
import { getColor } from "../../../../../../utils/getColor";

// styles
import css from "./Participation.module.scss";

// register chartjs plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface BarChartOptions extends ChartOptions<"bar"> {
	barThickness: number;
	barBorderRadius: number;
}

const ParticipationChart = () => {
	const [filter] = useStore((state) => [state.filter]);

	// api response for the date range
	const { data: participationData, isLoading } = useParticipationRate({
		startDate: filter.startDate,
		endDate: filter.endDate,
	});

	const dateRangeLabels = useMemo<MonthLabel[]>(() => {
		const monthlyAverages = participationData?.data?.monthlyAverages;
		const oldestDataDate =
			monthlyAverages && monthlyAverages.length > 0
				? new Date(monthlyAverages[monthlyAverages.length - 1].time_bucket)
				: null;
		return getParticipationDateRangeLabels({
			filterStartDate: filter.startDate,
			filterEndDate: filter.endDate,
			oldestDataDate,
		});
	}, [filter.startDate, filter.endDate, participationData]);

	const getCounts = (responseType) => {
		if (participationData?.data?.monthlyAverages.length) {
			const responseCountsMap = participationData?.data?.monthlyAverages.reduce((acc, data) => {
				const monthBucket = format(data.time_bucket, "yyyy-MM");
				const percentageResponded = Math.round((data[responseType] / data.total_surveys) * 100);
				return {
					...acc,
					[monthBucket]: percentageResponded,
				};
			}, {});
			return dateRangeLabels.map((month) => {
				return responseCountsMap[month.value] || null;
			});
		}
		return [];
	};

	const responseCounts = useMemo(() => {
		return getCounts("response_count");
	}, [participationData]);

	const noResponseCounts = useMemo(() => {
		return getCounts("no_response_count");
	}, [participationData]);

	const data: ChartData<"bar"> = {
		labels: dateRangeLabels.map((month) => month.label),
		datasets: [
			{
				label: "Participation Rate",
				data: responseCounts,
				backgroundColor: getColor("primary", 5),
				borderWidth: 0,
			},
			{
				label: "Bar Background",
				data: noResponseCounts.map((count) => (count ? 100 : 0)),
				backgroundColor: getColor("neutral", 2),
				borderWidth: 0,
				// animation: false,
			},
		],
	};

	// chart options
	const options: BarChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		barThickness: 8,
		barBorderRadius: 2,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				yAlign: "center",
				displayColors: false,
				backgroundColor: getColor("neutral", 7),
				titleFont: {
					weight: "normal",
					family: "Source Sans Pro, sans-serif",
					size: 12,
				},
				bodyFont: {
					family: "Source Sans Pro, sans-serif",
					size: 12,
				},
				caretPadding: 8,
				cornerRadius: 4,
				callbacks: {
					// format tooltip title
					title: (context) => {
						return context[0].label.split(",").join(" ");
					},
					label: (context) => {
						// display no_response count for background gray bar
						if (context.datasetIndex === 1) {
							return noResponseCounts[context.dataIndex]
								? `${noResponseCounts[context.dataIndex] || 0}%`
								: "";
						}
						return context.raw ? `${context.raw || 0}%` : "";
					},
				},
			},
		},
		scales: {
			x: {
				border: {
					color: getColor("neutral", 1),
					width: 1,
				},
				grid: {
					color: getColor("neutral", 1),
					drawTicks: false,
				},
				ticks: {
					padding: 8,
					maxRotation: 0,
					minRotation: 0,
					color: getColor("primary", 9),
					font: {
						size: 10,
						family: "Source Sans Pro, sans-serif",
					},
				},
				stacked: true,
			},
			y: {
				display: true,
				min: 0,
				max: 100,
				border: {
					color: getColor("neutral", 1),
					width: 1,
				},
				grid: {
					color: getColor("neutral", 1),
					drawTicks: false,
				},
				ticks: {
					padding: 8,
					color: getColor("primary", 9),
					font: {
						size: 10,
						family: "Source Sans Pro, sans-serif",
					},
					stepSize: 20,
					callback: (value) => {
						return `${value || 0}%`;
					},
				},
				stacked: true,
			},
		},
	};

	if (isLoading) {
		return (
			<div className={css.spinnerBox}>
				<Spinner size={20} />
			</div>
		);
	}

	if (participationData?.data?.monthlyAverages?.length === 0) {
		return <EmptyChart type="participation" />;
	}

	return (
		<div className={css.participationChart}>
			<div className={css.barChart}>
				<Bar data={data} options={options} />
			</div>
			<div className={css.legendContainer}>
				<Text
					className={`${css.legendText} ${css.notResponded}`}
					element="span"
					size="extra-small"
					weight="semi-bold"
				>
					Not Responded
				</Text>
				<Text
					className={`${css.legendText} ${css.responded}`}
					element="span"
					size="extra-small"
					weight="semi-bold"
				>
					Responded
				</Text>
			</div>
		</div>
	);
};

export default ParticipationChart;
